import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { styled, css, Collapse, Menu } from '@mui/material'
import { connect } from 'react-redux'
import { geoLocation, getCurrentLocation } from '@helpers/geo-location'
import { announce } from '@helpers/aria-announce'
import { setShippingAddress } from '@redux/modules/location'
import { saveLocalStorage, removeLocalStorage } from '@helpers/storage'
import LocationFields from '@shared/location-fields'
import { useApolloClient } from '@apollo/client'
import { SESSION_MY_STORE_STRING } from '../../../lib/hooks/useMyStore'

const Container = styled('div')(
  ({ open, className }) => css`
    display: flex;
    flex-direction: column;
    & > div {
      margin: 0 auto;
      padding: 0;
      width: ${className === 'header' ? '100%' : '90%'};
    }
  `,
)
const MenuContainer = styled(Container)({
  padding: '10px 0',
})
const Body = styled('div')`
  border-bottom: 0;
  padding-top: 5px;
  text-align: center;
`
const Description = styled('p')`
  font-size: 16px;
  text-align: left;
  margin-bottom: 10px;
`

const ShippingChangeZip = ({
  variant,
  anchorRef,
  onClose,
  className,
  open,
  shipping_address,
  onSetShippingAddress,
  cbFunction,
}) => {
  const [addressInput, setAddressInput] = useState('')
  const [error, setError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const addrArr = shipping_address ? shipping_address.split(',') : []
  const renderAsMenu = ['pdp', 'plp'].includes(variant)
  const apolloClient = useApolloClient()

  const locationInputRef = useRef(null)
  useEffect(() => {
    if (locationInputRef.current) {
      locationInputRef.current.focus()
    }
  }, [])

  const handleSubmit = async event => {
    event.preventDefault()
    setIsLoading(true)
    const isPRZip =
      (Number(addressInput) >= 600 && Number(addressInput) <= 799) ||
      (Number(addressInput) >= 900 && Number(addressInput) <= 999)

    /* explicitly define the allowed URLs */
    const allowedUSDevHosts = ['rtg-dev.com', 'www.rtg-dev.com']
    const allowedUSProdHosts = ['roomstogo.com', 'www.roomstogo.com']

    /* grab the domain from the URL */
    let windowHost = ''
    if (typeof window !== 'undefined') {
      windowHost = window.location.hostname
    }

    /* if the user is on .com dev or prod and enters a Puerto Rico zipcode, redirect to the PR website */
    if (allowedUSDevHosts.includes(windowHost) && isPRZip) {
      window.location.href = `https://www.rtg-dev.pr?zip=${Number(addressInput)}`
    } else if (allowedUSProdHosts.includes(windowHost) && isPRZip) {
      window.location.href = `https://www.roomstogo.pr?zip=${Number(addressInput)}`
    } else {
      const currentZip = getCurrentLocation().zip
      if (cbFunction && typeof cbFunction === 'function') cbFunction()
      const err = await geoLocation(apolloClient, addressInput)
      if (err) {
        announce('Error updating location')
        setError(true)
        locationInputRef?.current?.focus()
      } else {
        removeLocalStorage(SESSION_MY_STORE_STRING)
        saveLocalStorage('changeLocReturn', 'changeLocHeaderBtn')
      }
    }
    setIsLoading(false)
  }

  const onChange = event => {
    setError(false)
    setAddressInput(event.target.value)
  }

  useEffect(() => {
    if (addrArr[0] === '') {
      onSetShippingAddress(addrArr[1])
    }
  }, [addrArr, onSetShippingAddress])

  return renderAsMenu ? (
    <Menu
      open={open}
      anchorEl={anchorRef.current}
      onClose={onClose}
      MenuListProps={{
        'aria-labelledby': 'plp-deliver-menu-btn',
        as: 'div',
      }}
    >
      <MenuContainer>
        <Body>
          <Description>View product availability in your area by entering delivery location.</Description>
          <div className="form">
            <form onSubmit={e => handleSubmit(e)}>
              <LocationFields
                inputRef={locationInputRef}
                addressInput={addressInput}
                error={error}
                id="Header"
                noAutoComplete
                onChange={onChange}
                isLoading={isLoading}
              />
            </form>
          </div>
        </Body>
      </MenuContainer>
    </Menu>
  ) : (
    <Container open={open} className={className}>
      <Body>
        <Collapse in={open}>
          <Description>View product availability in your area by entering delivery location.</Description>
          <div className="form">
            <form onSubmit={e => handleSubmit(e)}>
              <LocationFields
                inputRef={locationInputRef}
                addressInput={addressInput}
                error={error}
                id="Header"
                noAutoComplete
                onChange={onChange}
                isLoading={isLoading}
              />
            </form>
          </div>
        </Collapse>
      </Body>
    </Container>
  )
}

ShippingChangeZip.propTypes = {
  variant: PropTypes.string,
  onClose: PropTypes.func,
  anchorRef: PropTypes.element,
  open: PropTypes.bool,
  className: PropTypes.string,
  onSetShippingAddress: PropTypes.func,
  shipping_address: PropTypes.string,
  cbFunction: PropTypes.func,
}

const mapStateToProps = state => ({ ...state.location })

const mapDispatchToProps = dispatch => ({
  onSetShippingAddress: shipping_address => dispatch(setShippingAddress(shipping_address)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ShippingChangeZip)
